import { useState } from 'react';
import { TrendingUp, ChevronDown, Heart, Mountain, Footprints, Compass, Twitter, BarChart3 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { BrandPulseLiveFeed } from './BrandPulseLiveFeed';
import { BrandPulseTopInfluencers } from './BrandPulseTopInfluencers';
import { BrandPulseBrandTracker } from './BrandPulseBrandTracker';

export function BrandPulsePage() {
  const [activeTab, setActiveTab] = useState<'brand-tracker' | 'brand-health' | 'live-feed' | 'top-influencers'>('brand-tracker');
  const [selectedTimeRange, _setSelectedTimeRange] = useState('Last 12 months');

  // Product sentiment data - Merrell Products
  const productSentiment = [
    {
      id: 1,
      name: 'Moab 3 Hiking Boot',
      image: 'https://s7d4.scene7.com/is/image/WolverineWorldWide/MRLW-J036344-WHT-040722-S22-000?$dw-pdp-primary$',
      positive: 92,
      neutral: 6,
      negative: 2
    },
    {
      id: 2,
      name: 'Agility Peak 5',
      image: 'https://s7d4.scene7.com/is/image/WolverineWorldWide/MRLM-J068041-081722-F23-000?$dw-pdp-primary$',
      positive: 88,
      neutral: 9,
      negative: 3
    },
    {
      id: 3,
      name: 'Moab Speed 2',
      image: 'https://s7d4.scene7.com/is/image/WolverineWorldWide/MRLM-J038329-090424-F25-000?$dw-pdp-primary$',
      positive: 85,
      neutral: 11,
      negative: 4
    },
    {
      id: 4,
      name: 'ProMorph',
      image: 'https://s7d4.scene7.com/is/image/WolverineWorldWide/MRLM-J00005111-042125-S26-000?$dw-pdp-primary$',
      positive: 81,
      neutral: 15,
      negative: 4
    },
    {
      id: 5,
      name: 'Thermo Snow Grip',
      image: 'https://s7d4.scene7.com/is/image/WolverineWorldWide/MRLW-J038756-112524-F25-000?$dw-pdp-primary$',
      positive: 78,
      neutral: 17,
      negative: 5
    }
  ];

  // Top fan segments
  const fanSegments = [
    { emoji: '⛰️', label: 'Trail Hikers', percentage: 48 },
    { emoji: '🏃', label: 'Trail Runners', percentage: 35 },
    { emoji: '🏕️', label: 'Outdoor Campers', percentage: 31 },
    { emoji: '🧗', label: 'Adventure Seekers', percentage: 22 }
  ];

  // Trending keywords
  const trendingKeywords = [
    'Waterproof', 'Gore-Tex', 'Trail running', 'Hiking boots',
    'Vibram sole', 'Lightweight', 'All-terrain', 'Comfort'
  ];

  // Sentiment split data for donut chart
  const sentimentData = [
    { name: 'Positive', value: 85, color: '#10b981' },
    { name: 'Neutral', value: 12, color: '#6b7280' },
    { name: 'Negative', value: 3, color: '#ef4444' }
  ];

  // Countries data
  const countriesData = [
    { country: 'United States', percentage: 52, flag: '🇺🇸' },
    { country: 'Canada', percentage: 18, flag: '🇨🇦' },
    { country: 'United Kingdom', percentage: 12, flag: '🇬🇧' },
    { country: 'Germany', percentage: 9, flag: '🇩🇪' },
    { country: 'Australia', percentage: 8, flag: '🇦🇺' },
    { country: 'Switzerland', percentage: 6, flag: '🇨🇭' },
    { country: 'Norway', percentage: 5, flag: '🇳🇴' }
  ];

  // News & influencer mentions
  const newsMentions = [
    {
      id: 1,
      source: '@TrailRunnerMag',
      text: 'Just completed a 50-mile trail race in my @Merrell Agility Peak 5s. Zero blisters, amazing grip on technical terrain! 🏃‍♂️⛰️ #trailrunning #merrell',
      platform: 'twitter',
      time: '2h ago',
      likes: 842
    },
    {
      id: 2,
      source: '@HikingAddict',
      text: 'The Moab 3 boots are absolutely game-changing for long hikes. 15 miles today and my feet feel fresh! Best investment this year. @Merrell',
      platform: 'twitter',
      time: '4h ago',
      likes: 623
    },
    {
      id: 3,
      source: '@OutdoorGearLab',
      text: 'After 6 months of testing, Merrell\'s Gore-Tex line stands out for durability and weather protection. Full review coming soon! 🥾',
      platform: 'twitter',
      time: '6h ago',
      likes: 1203
    }
  ];

  // Mentions chart data
  const mentionsChartData = [
    { month: 'Feb', mentions: 1850 },
    { month: 'Mar', mentions: 2100 },
    { month: 'Apr', mentions: 2450 },
    { month: 'May', mentions: 2680 },
    { month: 'Jun', mentions: 3200 },
    { month: 'Jul', mentions: 3890 }
  ];

  // Sources data for pie chart
  const sourcesData = [
    { name: 'Instagram', value: 45, color: '#e4405f' },
    { name: 'Twitter', value: 28, color: '#1da1f2' },
    { name: 'YouTube', value: 18, color: '#ff0000' },
    { name: 'Facebook', value: 9, color: '#1877f2' }
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa] p-2.5">
      <div className="min-h-[calc(100vh-20px)] rounded-2xl bg-white border border-gray-200/80 shadow-sm overflow-hidden">
        <div className="max-w-[1600px] mx-auto p-8">
          {/* Brand Hero Banner */}
          <div className="relative mb-8 rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-900/95 via-purple-800/90 to-purple-700/85 z-10"></div>
            <div 
              className="h-48 bg-cover bg-center"
              style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1603475429038-44361bcde123?w=1600)' }}
            ></div>
            <div className="absolute inset-0 z-20 flex items-center justify-between px-10">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
                  <Mountain className="w-10 h-10 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-white text-4xl font-bold">Merrell</h1>
                    <Badge className="bg-white/20 backdrop-blur-md text-white border-white/30">Outdoor Footwear</Badge>
                  </div>
                  <p className="text-white/90 text-lg">Where the trail begins</p>
                  <p className="text-white/70 text-sm mt-1">Real-time brand intelligence across outdoor communities worldwide</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-white/70 text-xs mb-1">Overall Health Score</div>
                  <div className="text-white text-4xl font-bold">89</div>
                  <div className="text-green-300 text-sm flex items-center justify-end gap-1 mt-1">
                    <TrendingUp className="w-4 h-4" />
                    +5.2 from last month
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Compass className="w-5 h-5 text-purple-600" />
                  <h2 className="text-gray-900">Brand Pulse Dashboard</h2>
                </div>
                <p className="text-sm text-gray-600">Track brand performance, sentiment, and community engagement across all channels</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400">Last updated: Jan 2026</span>
                <button className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                  {selectedTimeRange}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-6 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('brand-tracker')}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeTab === 'brand-tracker'
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Brand Tracker
              </button>
              <button
                onClick={() => setActiveTab('brand-health')}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeTab === 'brand-health'
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Brand Health
              </button>
              <button
                onClick={() => setActiveTab('live-feed')}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeTab === 'live-feed'
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Live Feed
              </button>
              <button
                onClick={() => setActiveTab('top-influencers')}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeTab === 'top-influencers'
                    ? 'border-gray-900 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Top Influencers
              </button>
            </div>
          </div>

          {/* Top Metrics */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow bg-gradient-to-br from-white to-purple-50/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Footprints className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="text-sm text-gray-600">Brand Awareness</div>
                </div>
                <div className="flex items-end justify-between">
                  <span className="text-3xl text-gray-900 font-bold">2.8M</span>
                  <span className="text-green-600 text-sm flex items-center gap-1 font-medium">
                    <TrendingUp className="w-4 h-4" />
                    +12.3%
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow bg-gradient-to-br from-white to-green-50/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                    <Heart className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="text-sm text-gray-600">Overall Sentiment</div>
                </div>
                <div className="flex items-end justify-between">
                  <span className="text-3xl text-gray-900 font-bold">85%</span>
                  <Badge className="bg-green-100 text-green-700 border-0 font-medium">Excellent</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow bg-gradient-to-br from-white to-blue-50/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-sm text-gray-600">Influence Score</div>
                </div>
                <div className="text-3xl text-gray-900 font-bold">89</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm hover:shadow-md transition-shadow bg-gradient-to-br from-white to-orange-50/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Mountain className="w-4 h-4 text-orange-600" />
                  </div>
                  <div className="text-sm text-gray-600">Top Product</div>
                </div>
                <div className="text-gray-900 font-bold mb-1">Moab 3 Boot</div>
                <div className="text-sm text-gray-500">24.3k mentions</div>
              </CardContent>
            </Card>
          </div>

          {/* Conditional Tab Content */}
          {activeTab === 'live-feed' && <BrandPulseLiveFeed />}
          {activeTab === 'top-influencers' && <BrandPulseTopInfluencers />}
          {activeTab === 'brand-tracker' && <BrandPulseBrandTracker />}

          {activeTab === 'brand-health' && (
            <>
              {/* Product Sentiment & Audience Pulse Row */}
              <div className="grid grid-cols-3 gap-6 mb-6">
                {/* Product Sentiment - Takes 2 columns */}
                <div className="col-span-2">
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-6">
                      <h3 className="text-gray-900 mb-6">Product Sentiment Analysis</h3>
                      <div className="space-y-6">
                        {productSentiment.map((product) => (
                          <div key={product.id} className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                              <ImageWithFallback
                                src={product.image}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex-1">
                              <div className="text-sm text-gray-900 mb-2 font-medium">{product.name}</div>
                              <div className="flex items-center gap-1 h-6">
                                {/* Positive */}
                                <div
                                  className="h-full bg-green-500 rounded-l-md flex items-center justify-center text-xs text-white font-medium"
                                  style={{ width: `${product.positive}%` }}
                                >
                                  {product.positive >= 20 && `${product.positive}%`}
                                </div>
                                {/* Neutral */}
                                <div
                                  className="h-full bg-gray-400 flex items-center justify-center text-xs text-white font-medium"
                                  style={{ width: `${product.neutral}%` }}
                                >
                                  {product.neutral >= 10 && `${product.neutral}%`}
                                </div>
                                {/* Negative */}
                                <div
                                  className="h-full bg-red-500 rounded-r-md flex items-center justify-center text-xs text-white font-medium"
                                  style={{ width: `${product.negative}%` }}
                                >
                                  {product.negative >= 5 && `${product.negative}%`}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Audience Pulse - Takes 1 column */}
                <div>
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-6">
                      <div className="mb-6">
                        <h3 className="text-gray-900 mb-1">Audience Pulse</h3>
                        <p className="text-xs text-gray-500">Key outdoor enthusiast segments</p>
                      </div>

                      {/* Top Fan Segments */}
                      <div className="mb-6">
                        <div className="text-sm text-gray-900 mb-3 font-medium">Top Fan Segments</div>
                        <div className="space-y-3">
                          {fanSegments.map((segment, index) => (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{segment.emoji}</span>
                                <span className="text-sm text-gray-700">{segment.label}</span>
                              </div>
                              <span className="text-sm text-gray-900 font-medium">{segment.percentage}%</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Trending Keywords */}
                      <div className="mb-6">
                        <div className="text-sm text-gray-900 mb-3 font-medium">Trending Keywords</div>
                        <div className="flex flex-wrap gap-2">
                          {trendingKeywords.map((keyword, index) => (
                            <Badge
                              key={index}
                              className="bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100"
                            >
                              {keyword}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* New Geo Opportunity */}
                      <div className="mt-6 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                        <div className="text-sm text-gray-900 mb-2 font-medium">🎯 New Geo Opportunity</div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">🇨🇭</span>
                            <span className="text-gray-900 font-medium">Switzerland</span>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-green-600 font-medium">+45% growth</div>
                            <div className="text-xs text-gray-500">Alpine market</div>
                          </div>
                        </div>
                        <Button className="w-full bg-purple-600 text-white hover:bg-purple-700 text-xs h-8">
                          View Market Insights
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Open Web Insights */}
              <div className="mb-6">
                <h2 className="text-gray-900 mb-2">Open Web Insights</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Real-world discussions about Merrell across outdoor communities and forums
                </p>

                <div className="grid grid-cols-4 gap-4">
                  {/* Mentions Over Time */}
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-6">
                      <div className="text-sm text-gray-500 mb-1">Monthly Mentions</div>
                      <div className="text-3xl text-gray-900 mb-1">3,890</div>
                      <div className="text-sm text-green-600">+690 from last month</div>
                    </CardContent>
                  </Card>

                  {/* Sentiment Split */}
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-6">
                      <div className="text-sm text-gray-900 mb-4 font-medium">Sentiment Split</div>
                      <div className="h-32 flex items-center justify-center">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={sentimentData}
                              cx="50%"
                              cy="50%"
                              innerRadius={30}
                              outerRadius={50}
                              paddingAngle={2}
                              dataKey="value"
                            >
                              {sentimentData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex items-center justify-center gap-4 text-xs mt-2">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-gray-600">85%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                          <span className="text-gray-600">12%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          <span className="text-gray-600">3%</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* News & Influencer Mentions */}
                  <Card className="border-0 shadow-sm col-span-2">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm text-gray-900 font-medium">Top Influencer Mentions</div>
                        <button className="text-xs text-purple-600 hover:text-purple-700 font-medium">
                          View all →
                        </button>
                      </div>
                      <div className="space-y-3">
                        {newsMentions.slice(0, 3).map((mention) => (
                          <div
                            key={mention.id}
                            className="pb-3 border-b border-gray-100 last:border-0"
                          >
                            <div className="flex items-start gap-2 mb-1">
                              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-xs text-white font-bold">
                                {mention.source.slice(1, 3).toUpperCase()}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm text-gray-900 font-medium">{mention.source}</span>
                                  <span className="text-xs text-gray-400">{mention.time}</span>
                                </div>
                                <p className="text-xs text-gray-600 line-clamp-2">
                                  {mention.text}
                                </p>
                                <div className="flex items-center gap-3 mt-2">
                                  <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <Heart className="w-3 h-3" />
                                    {mention.likes}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Countries Heatmap */}
                <Card className="border-0 shadow-sm mt-4">
                  <CardContent className="p-6">
                    <div className="text-sm text-gray-900 mb-4 font-medium">Geographic Distribution</div>
                    <div className="grid grid-cols-4 gap-4">
                      {countriesData.map((country, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{country.flag}</span>
                            <span className="text-sm text-gray-700">{country.country}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-purple-500 to-purple-600"
                                style={{ width: `${country.percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 w-8 font-medium">{country.percentage}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Social Media Overview */}
              <div>
                <h2 className="text-gray-900 mb-2">Social Media Overview</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Merrell's presence and engagement across outdoor lifestyle platforms
                </p>

                <div className="grid grid-cols-3 gap-6">
                  {/* Live Feed of Top Mentions */}
                  <Card className="border-0 shadow-sm col-span-2">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-gray-900">Live Feed of Top Mentions</h3>
                        <Badge className="bg-purple-100 text-purple-700 border-0">
                          <Mountain className="w-3 h-3 mr-1" />
                          Outdoor Community
                        </Badge>
                      </div>
                      <div className="space-y-4">
                        {newsMentions.map((mention) => (
                          <div
                            key={mention.id}
                            className="p-4 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-100 hover:border-purple-200 transition-all"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                                {mention.source.slice(1, 3).toUpperCase()}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm text-gray-900 font-medium">{mention.source}</span>
                                  <span className="text-xs text-gray-400">{mention.time}</span>
                                  <Twitter className="w-3 h-3 text-blue-400 ml-auto" />
                                </div>
                                <p className="text-sm text-gray-700 mb-2">{mention.text}</p>
                                <div className="flex items-center gap-4">
                                  <button className="text-xs text-gray-500 hover:text-red-500 flex items-center gap-1">
                                    <Heart className="w-3 h-3" />
                                    {mention.likes}
                                  </button>
                                  <button className="text-xs text-gray-500 hover:text-purple-600">Reply</button>
                                  <button className="text-xs text-gray-500 hover:text-purple-600">Share</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Right Column - Charts */}
                  <div className="space-y-6">
                    {/* Mentions Chart */}
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm text-gray-900 font-medium">Mentions Growth</h3>
                          <div className="text-xs text-green-600 font-medium">+690 this month</div>
                        </div>
                        <div className="text-3xl text-gray-900 mb-4">3,890</div>
                        <ResponsiveContainer width="100%" height={120}>
                          <LineChart data={mentionsChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                            <XAxis
                              dataKey="month"
                              stroke="#9ca3af"
                              tick={{ fontSize: 10 }}
                              axisLine={false}
                            />
                            <YAxis hide />
                            <Line
                              type="monotone"
                              dataKey="mentions"
                              stroke="#be43ff"
                              strokeWidth={3}
                              dot={{ fill: '#be43ff', strokeWidth: 2, r: 4 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>

                    {/* Sources */}
                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-6">
                        <h3 className="text-sm text-gray-900 mb-4 font-medium">Platform Sources</h3>
                        <div className="h-40 flex items-center justify-center mb-4">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={sourcesData}
                                cx="50%"
                                cy="50%"
                                innerRadius={40}
                                outerRadius={60}
                                paddingAngle={2}
                                dataKey="value"
                              >
                                {sourcesData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="space-y-2">
                          {sourcesData.map((source, index) => (
                            <div key={index} className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: source.color }}
                                ></div>
                                <span className="text-gray-700">{source.name}</span>
                              </div>
                              <span className="text-gray-900 font-medium">{source.value}%</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}